import React from 'react';
import { Modal, Button, Table } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { sarabunFont } from '../../sarabunFont'; // Import your font data
import { useTranslation } from 'react-i18next';

const ScheduleModal = ({ isVisible, onClose, popupData = [] }) => {
  const { t } = useTranslation();
  
  // Sort the data by date first, then by time
  const sortedData = [...(Array.isArray(popupData) ? popupData : [])].sort((a, b) => {
    const dateTimeA = new Date(`${a.date}T${a.schedule_time || '00:00'}`);
    const dateTimeB = new Date(`${b.date}T${b.schedule_time || '00:00'}`);
    return dateTimeA - dateTimeB;
  });
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) return 'Invalid Date'; // Handle invalid dates
  
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short', // 'long' ensures full month name (e.g., "January")
      year: 'numeric'
    }).format(date);
  };
  

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  const formatHours = (hoursValue) => {
    if (!hoursValue) return '';
    
    // If it's in "2h 30m" format, convert to "2:30 hr"
    if (typeof hoursValue === 'string' && hoursValue.includes('h')) {
      // Extract hours and minutes using regex
      const hourMatch = hoursValue.match(/(\d+)h/);
      const minuteMatch = hoursValue.match(/(\d+)m/);
      
      const hours = hourMatch ? parseInt(hourMatch[1], 10) : 0;
      const minutes = minuteMatch ? parseInt(minuteMatch[1], 10) : 0;
      
      if (hours === 0 && minutes === 0) return '';
      
      // Format as "H:MM hr"
      const formattedMinutes = minutes.toString().padStart(2, '0');
      const hourText = (hours === 1 && minutes === 0) ? 'hr' : 'hr';
      
      return `${hours}:${formattedMinutes} ${hourText}`;
    }
    
    // If it's a number, treat it as hours
    if (typeof hoursValue === 'number') {
      return `${hoursValue}:00 hr`;
    }
    
    return hoursValue.toString();
  };

  // Function to load Sarabun font into jsPDF
  const loadSarabunFont = (doc) => {
    doc.addFileToVFS('Sarabun.ttf', sarabunFont.src.split('base64,')[1]);
    doc.addFont('Sarabun.ttf', 'Sarabun', 'normal');
    doc.setFont('Sarabun');
  };

  const handleDownloadPDF = () => {
  const doc = new jsPDF();
  loadSarabunFont(doc);

  const margin = 20;
  const keyColor = '#95101e';
  const pageWidth = doc.internal.pageSize.getWidth();

  const headerImage = new Image();
  headerImage.src = '/images/advisorlogo.png';

  headerImage.onload = () => {
    // Header box dimensions
    const headerBoxHeight = 35;
    const headerBoxY = margin;
    
    // Draw opaque background box for header
    doc.setFillColor(245, 245, 245); // Light gray background
    doc.rect(margin, headerBoxY, pageWidth - (margin * 2), headerBoxHeight, 'F');
    
    // Add subtle border to the header box
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.5);
    doc.rect(margin, headerBoxY, pageWidth - (margin * 2), headerBoxHeight, 'S');

    // Calculate logo dimensions and position
    const imgAspectRatio = headerImage.width / headerImage.height;
    const logoHeight = 25;
    const logoWidth = logoHeight * imgAspectRatio;
    const logoX = margin + 10;
    const logoY = headerBoxY + 5;

    // Add logo
    doc.addImage(headerImage, 'PNG', logoX, logoY, logoWidth, logoHeight);

    // Contact information on the right side
    const contactX = pageWidth - margin - 10;
    const contactStartY = headerBoxY + 8;
    
    // Company/Institute name
    doc.setFontSize(12);
    doc.setTextColor(keyColor);
    doc.setFont('helvetica', 'bold');
    doc.text('The Advisor Academy', contactX, contactStartY, { align: 'right' });
    
    // Address
    doc.setFontSize(9);
    doc.setTextColor(51, 51, 51);
    doc.setFont('helvetica', 'normal');
    doc.text('1402 Siamscape 14th floor, Pathum Wan, ', contactX, contactStartY + 6, { align: 'right' });
    doc.text('Bangkok 10330, Thailand', contactX, contactStartY + 12, { align: 'right' });
    
    // Contact information
    doc.setFontSize(9);
    doc.setTextColor(keyColor);
    doc.text('Tel: +66 083 628 9191', contactX, contactStartY + 18, { align: 'right' });

 // Large class code below header box with more space
const centerX = pageWidth / 2;
const classCode = popupData[0]?.class_code || 'N/A';

// Set maximum width for class code (e.g., 80% of page width)
const maxClassCodeWidth = pageWidth - (margin * 2);

doc.setFontSize(20);
doc.setTextColor(keyColor);
doc.setFont('helvetica', 'bold');

// Split text if it's too long
const wrappedClassCode = doc.splitTextToSize(classCode, maxClassCodeWidth);

// Calculate starting Y position for class code
const classCodeStartY = headerBoxY + headerBoxHeight + 15;

// Draw the class code (will be centered and wrapped if necessary)
doc.text(wrappedClassCode, centerX, classCodeStartY, { align: 'center' });

// Calculate the height of the wrapped class code
// Font size 20 with typical line height factor of 1.15
const lineHeight = 20 * 1.15 / (72 / 25.4); // Convert to PDF units
const classCodeHeight = wrappedClassCode.length * lineHeight;

// Get first and last dates from sorted data
const sortedDates = popupData
  .map(item => new Date(item.date))
  .sort((a, b) => a - b);
const startDate = formatDate(sortedDates[0]);
const endDate = formatDate(sortedDates[sortedDates.length - 1]);

// Display dates with larger font and bold - adjusted Y position
const datesY = classCodeStartY + classCodeHeight + 10; // Add some spacing
doc.setFontSize(14);
doc.setFont('helvetica', 'bold');
doc.setTextColor(51, 51, 51);
doc.text(`Start Date: ${startDate}`, margin, datesY); 
doc.text(`End Date: ${endDate}`, centerX + 20, datesY);

// Subject line closer to dates with key color - adjusted Y position
const subjects = [...new Set(popupData.map(slot => slot.subject_name))].join(', ');
const maxTextWidth = doc.internal.pageSize.getWidth() - margin * 2;
const subjectText = `Subject(s): ${subjects}`;
const wrappedText = doc.splitTextToSize(subjectText, maxTextWidth);

doc.setFontSize(12);
doc.setTextColor(keyColor);
doc.text(wrappedText, margin, datesY + 10);

// Table starts after the header info - adjusted Y position
const tableStartY = datesY + 20;

    const columns = [
      { header: t('day'), dataKey: 'schedule_day' },
      { header: t('date'), dataKey: 'date', render: formatDate },
      {
        header: t('time'),
        dataKey: 'schedule_time',
        render: (text, record) => `${formatTime(record.schedule_time)} - ${formatTime(record.end_time)}`
      },
      { header: t('subjectName'), dataKey: 'subject_name' },
      { header: t('teacherName'), dataKey: 'nickname' },
      { header: t('hours'), dataKey: 'schedule_hour', render: formatHours },
      { header: t('classroomNumber'), dataKey: 'classroom_number' }
    ];

    doc.autoTable({
      startY: tableStartY,
      head: [columns.map(col => col.header)],
      body: sortedData.map(row => 
        columns.map(col => col.render ? col.render(row[col.dataKey], row) : row[col.dataKey])
      ),
      theme: 'grid',
      styles: {
        font: 'Sarabun',
        fontStyle: 'normal',
        fontSize: 10,
        textColor: [51, 51, 51],
        lineColor: [200, 200, 200],
        cellPadding: 2,
        overflow: 'linebreak',
        halign: 'left'
      },
      headStyles: {
        fillColor: [149, 16, 30],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10
      },
      alternateRowStyles: {
        fillColor: [252, 252, 252]
      },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 25 },
        2: { cellWidth: 25, fontSize: 10 },
        3: { 
          cellWidth: 45,
          overflow: 'linebreak',
          fontSize: 10,
          halign: 'center'
        },
        4: { cellWidth: 27, fontSize: 10 },
        5: { cellWidth: 16, fontSize: 10 },
        6: { cellWidth: 25, fontSize: 8 }
      },
      margin: { top: 20, left: 10, right: 10 }
    });

    // Page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for(let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setTextColor(128, 128, 128);
      doc.setFontSize(8);
      doc.text(
        `Page ${i} of ${pageCount}`, 
        doc.internal.pageSize.getWidth() / 2, 
        doc.internal.pageSize.getHeight() - 10, 
        { align: 'center' }
      );
    }

    doc.save(`${classCode}.pdf`);
  };

  headerImage.onerror = () => {
    console.error('Failed to load the header image.');
  };
};

  const columns = [
    { title: t('day'), dataIndex: 'schedule_day', key: 'schedule_day' },
    { title: t('date'), dataIndex: 'date', key: 'date', render: formatDate },
    {
      title: t('time'),
      key: 'time',
      render: (text, record) => `${formatTime(record.schedule_time)} - ${formatTime(record.end_time)}`
    },
    { title: t('subjectName'), dataIndex: 'subject_name', key: 'subject_name', align: 'center' },
    { title: t('teacherName'), dataIndex: 'nickname', key: 'nickname' },

    { title: t('hours'), dataIndex: 'schedule_hour', key: 'schedule_hour', render: formatHours },
    { title: t('classroomNumber'), dataIndex: 'classroom_number', key: 'classroom_number' }
  ];

  return (
    <Modal
      open={isVisible}
      onCancel={onClose}
      footer={[
        <Button key="download" onClick={handleDownloadPDF}>
          <FontAwesomeIcon icon={faDownload} /> {t('downloadPDF')}
        </Button>
      ]}
      title={sortedData?.[0]?.class_code || t('scheduleDetails')}
      width={800}
    >
      <Table
        dataSource={(popupData ?? []).sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);

          if (isNaN(dateA) || isNaN(dateB)) {
            return 0; // skip bad dates
          }

          if (dateA.getTime() !== dateB.getTime()) {
            return dateA - dateB;
          }

          // If same date, compare time
          const timeA = a.schedule_time ? a.schedule_time.split(":").map(Number) : [0, 0];
          const timeB = b.schedule_time ? b.schedule_time.split(":").map(Number) : [0, 0];

          const hourDiff = timeA[0] - timeB[0];
          return hourDiff !== 0 ? hourDiff : timeA[1] - timeB[1];
        })}
        columns={columns}
        rowKey={(record) => record.id}
        pagination={false}
        scroll={{ x: 'max-content' }}
      />
    </Modal>
  );
};
export default ScheduleModal;